import { useState, useEffect, useRef, useMemo } from 'react';
import { Brain, Trophy, Clock, Users, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { FirebaseError } from 'firebase/app';
import {
  TriviaGame,
  TriviaPlayerState,
  joinGame,
  submitAnswer,
  subscribeToMyPlayerState,
  getLeaderboard,
  seededShuffle,
} from '../../services/trivia';
import type { SongRequest } from '../../services/requests';

interface TriviaPlayModalProps {
  game: TriviaGame;
  eventId: string;
  attendeeId: string;
  nickname?: string;
  playedSongs: SongRequest[];
  onClose: () => void;
}

export default function TriviaPlayModal({
  game,
  eventId,
  attendeeId,
  nickname,
  playedSongs,
  onClose,
}: TriviaPlayModalProps) {
  const [playerState, setPlayerState] = useState<TriviaPlayerState | null>(null);
  const [joining, setJoining] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [selectedSongId, setSelectedSongId] = useState<string | null>(null);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [leaderboard, setLeaderboard] = useState<TriviaPlayerState[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastQuestionIndexRef = useRef(-1);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!game?.id) return;
    const unsub = subscribeToMyPlayerState(game.id, attendeeId, setPlayerState);
    return () => unsub();
  }, [game?.id, attendeeId]);

  useEffect(() => {
    if (game.currentQuestionIndex !== lastQuestionIndexRef.current) {
      lastQuestionIndexRef.current = game.currentQuestionIndex;
      setAnswered(false);
      setSelectedSongId(null);
      setAnswerCorrect(null);
    }
  }, [game.currentQuestionIndex]);

  useEffect(() => {
    if (game.status === 'active' && game.currentQuestionStartedAt) {
      const startedAt = new Date(game.currentQuestionStartedAt).getTime();
      const endAt = startedAt + game.questionTimeSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
        setTimeLeft(remaining);
        if (remaining <= 0 && timerRef.current) {
          clearInterval(timerRef.current);
        }
      };

      tick();
      timerRef.current = setInterval(tick, 250);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    } else if (game.status === 'review') {
      const createdAt = new Date(game.updatedAt).getTime();
      const endAt = createdAt + game.reviewDurationSeconds * 1000;

      const tick = () => {
        const remaining = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
        setTimeLeft(remaining);
      };

      tick();
      timerRef.current = setInterval(tick, 250);
      return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }
  }, [game.status, game.currentQuestionStartedAt, game.updatedAt, game.questionTimeSeconds, game.reviewDurationSeconds]);

  useEffect(() => {
    if (game.status === 'revealing' || game.status === 'completed') {
      getLeaderboard(game.id).then(setLeaderboard).catch(() => {});
    }
  }, [game.status, game.id]);

  const shuffledChoices = useMemo(() => {
    if (!game.currentChoices) return [];
    return seededShuffle(game.currentChoices, attendeeId + game.id + game.currentQuestionIndex);
  }, [game.currentChoices, attendeeId, game.id, game.currentQuestionIndex]);

  function getSongInfo(songRequestId: string): { title: string; artist: string; albumArtUrl?: string } {
    const song = playedSongs.find(s => s.id === songRequestId);
    if (song) {
      return { title: song.song.title, artist: song.song.artist, albumArtUrl: song.song.albumArtUrl };
    }
    return { title: 'Unknown Song', artist: 'Unknown Artist' };
  }

  async function handleJoin() {
    setJoining(true);
    try {
      await joinGame(game.id, eventId, attendeeId, nickname);
    } catch (error) {
      toast.error('Failed to join game');
    } finally {
      setJoining(false);
    }
  }

  function handleDismiss() {
    dismissedRef.current = true;
    onClose();
  }

  async function handleSelectAnswer(songId: string) {
    if (answered || submitting || timeLeft <= 0) return;
    if (playerState?.eliminated) return;

    setSelectedSongId(songId);
    setSubmitting(true);

    try {
      const result = await submitAnswer(game.id, game.currentQuestionIndex, songId);
      setAnswered(true);
      setAnswerCorrect(result.correct);

      if (result.correct) {
        navigator.vibrate?.(100);
        toast.success('Correct!');
      } else {
        navigator.vibrate?.([50, 50, 50]);
        toast.error(result.eliminated ? "Wrong! You're eliminated." : 'Wrong!');
      }
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'functions/failed-precondition') {
        toast.error(error.message);
      } else {
        toast.error('Failed to submit answer');
      }
      setSelectedSongId(null);
    } finally {
      setSubmitting(false);
    }
  }

  const ruleSetDescriptions: Record<string, string> = {
    first_correct: 'First to answer correctly wins!',
    most_correct: 'Answer the most questions correctly to win!',
    sudden_death: 'Wrong answer = elimination! Last one standing wins.',
  };

  // --- Join Prompt ---
  if (!playerState?.joined) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center space-y-4">
          <Brain className="w-12 h-12 text-purple-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Music Trivia!</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{ruleSetDescriptions[game.ruleSet]}</p>
          <div className="flex items-center justify-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" /> {game.playerCount} players are playing
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleJoin}
              disabled={joining}
              className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 disabled:opacity-50"
            >
              {joining ? 'Joining...' : 'Join Game'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Review Phase (sudden death) ---
  if (game.status === 'review') {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex flex-col">
        <div className="bg-white dark:bg-gray-900 flex-1 overflow-y-auto p-4 pt-safe">
          <div className="max-w-sm mx-auto space-y-4">
            <div className="text-center">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Study the Playlist!</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Questions start in {timeLeft}s</p>
            </div>

            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all duration-250"
                style={{ width: `${(timeLeft / game.reviewDurationSeconds) * 100}%` }}
              />
            </div>

            <div className="space-y-2">
              {playedSongs.map(song => (
                <div key={song.id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {song.song.albumArtUrl && (
                    <img src={song.song.albumArtUrl} alt="" className="w-10 h-10 rounded object-cover" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{song.song.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{song.song.artist}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Eliminated ---
  if (playerState?.eliminated) {
    if (game.status === 'revealing' || game.status === 'completed') {
      return renderResults();
    }

    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center space-y-4">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Eliminated!</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You were eliminated in question {(playerState.eliminatedAtQuestion ?? 0) + 1}
          </p>
          <div className="flex items-center justify-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" /> {game.activePlayers} {game.activePlayers !== 1 ? 'players remain' : 'player remains'}
          </div>

          {/* Spectator view of current question */}
          {game.status === 'active' && game.currentClueText && (
            <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current question:</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 italic">{game.currentClueText}</p>
            </div>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // --- Results ---
  if (game.status === 'revealing' || game.status === 'completed') {
    return renderResults();
  }

  // --- Waiting Between Questions ---
  if (game.status === 'between_questions') {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center space-y-4">
          <Clock className="w-10 h-10 text-purple-500 mx-auto animate-pulse" />
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Waiting for next question...</h2>

          {game.revealedCorrectSongId && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              The answer was "{getSongInfo(game.revealedCorrectSongId).title}"
            </p>
          )}

          {game.ruleSet === 'most_correct' && playerState && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your score: {playerState.correctCount} correct
            </p>
          )}
          {game.ruleSet === 'sudden_death' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {game.activePlayers} player{game.activePlayers !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>
      </div>
    );
  }

  // --- Active Question ---
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex flex-col">
      <div className="bg-white dark:bg-gray-900 flex-1 flex flex-col overflow-hidden">
        {/* Progress Bar */}
        <div className="flex-shrink-0">
          <div className="flex gap-1 p-2">
            {Array.from({ length: game.totalQuestions }, (_, i) => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full ${
                  i < game.currentQuestionIndex ? 'bg-purple-500' :
                  i === game.currentQuestionIndex ? 'bg-purple-300' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            ))}
          </div>
          <div className="w-full h-1 bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full transition-all duration-250 ${timeLeft <= 3 ? 'bg-red-500' : 'bg-purple-500'}`}
              style={{ width: `${(timeLeft / game.questionTimeSeconds) * 100}%` }}
            />
          </div>
        </div>

        {/* Question Info */}
        <div className="flex-shrink-0 p-4 pb-2 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Question {game.currentQuestionIndex + 1} of {game.totalQuestions} · {timeLeft}s
          </p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white leading-snug">
            {game.currentClueText}
          </p>
        </div>

        {/* Answer Buttons */}
        <div className="flex-1 p-4 pt-2 flex flex-col gap-3 justify-center">
          {shuffledChoices.map(songId => {
            const song = getSongInfo(songId);
            const isSelected = selectedSongId === songId;
            const showResult = answered && isSelected;

            let borderClass = 'border-gray-200 dark:border-gray-700';
            if (showResult && answerCorrect) borderClass = 'border-green-400 bg-green-50 dark:bg-green-900/20';
            if (showResult && !answerCorrect) borderClass = 'border-red-400 bg-red-50 dark:bg-red-900/20';

            return (
              <button
                key={songId}
                onClick={() => handleSelectAnswer(songId)}
                disabled={answered || submitting || timeLeft <= 0}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${borderClass} ${
                  !answered && !submitting && timeLeft > 0
                    ? 'hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10 active:scale-[0.98]'
                    : 'opacity-70'
                }`}
              >
                {song.albumArtUrl && (
                  <img src={song.albumArtUrl} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="text-left min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">{song.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{song.artist}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  function renderResults() {
    const isWinner = game.winnerIds.includes(attendeeId);
    const myRank = leaderboard.findIndex(p => p.attendeeId === attendeeId) + 1;

    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="text-center">
            <Trophy className={`w-12 h-12 mx-auto mb-2 ${isWinner ? 'text-amber-500' : 'text-gray-400'}`} />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isWinner ? 'You Won!' : 'Game Over'}
            </h2>
          </div>

          {game.winnerNicknames.length > 0 && !isWinner && (
            <div className="text-center p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Winner</p>
              <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{game.winnerNicknames[0]}</p>
            </div>
          )}

          {/* Leaderboard */}
          {game.ruleSet === 'most_correct' && leaderboard.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Top 10</p>
              {leaderboard.map((p, i) => (
                <div key={p.id} className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                  p.attendeeId === attendeeId
                    ? 'bg-purple-50 dark:bg-purple-900/20 ring-1 ring-purple-300 dark:ring-purple-600'
                    : i === 0 ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-gray-50 dark:bg-gray-800'
                }`}>
                  <span className="text-gray-700 dark:text-gray-300">
                    {i + 1}. {attendeeDisplayName(p.nickname, p.attendeeId)}
                    {p.attendeeId === attendeeId && ' (you)'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {p.correctCount}/{game.questionCount} · {(p.cumulativeTimeMs / 1000).toFixed(1)}s
                  </span>
                </div>
              ))}
              {myRank > 10 && (
                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-1">Your rank: #{myRank}</p>
              )}
            </div>
          )}

          {/* Player stats */}
          {playerState && (
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              You answered {playerState.correctCount} of {game.questionCount} correctly
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
}
